/**
 * KnowledgeReviewPage.tsx — Owner-only Knowledge Review admin page (Gap 2, Block E)
 *
 * Lists pending fix_knowledge records queued for owner review.
 * Each record was flagged at write time because its cosine similarity to existing
 * records was below 0.25 (potential outlier / poisoning attempt).
 *
 * The owner can Approve (flip pending → active) or Reject (flip pending → rejected).
 * Cross-shop safety is enforced at the API layer — this page cannot access
 * another shop's records even if the request is tampered.
 *
 * Route: /owner/settings/knowledge-review
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Card,
  List,
  Button,
  Space,
  Tag,
  Empty,
  Spin,
  App,
  Descriptions,
} from 'antd';
import { CheckOutlined, CloseOutlined, DatabaseOutlined } from '@ant-design/icons';
import { getTokens } from '../../services/authApi';

const { Title, Text, Paragraph } = Typography;

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:3001/api';

interface PendingRecord {
  id: string;
  symptom_text: string;
  cause_text: string;
  correction_text: string;
  vehicle_year: number;
  vehicle_make: string;
  vehicle_model: string;
  mileage_band: string;
  dtc_codes: string;
  outcome: string;
  created_at: string;
  /** Max similarity score at write time (from FixKnowledge.write outlier check). */
  _score?: number;
}

function getAuthHeader(): HeadersInit {
  // Staff/owner tokens are stored under `cnf_auth_tokens` (JSON blob) by authApi,
  // NOT under a bare `token` key. Read the real access token or auth fails 401.
  const token = getTokens()?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const KnowledgeReviewPage: React.FC = () => {
  const { message } = App.useApp();
  const [records, setRecords] = useState<PendingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/knowledge-review`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setRecords(data.pending ?? []);
    } catch (err) {
      message.error('Failed to load pending records');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleAction = async (id: string, action: 'approve' | 'reject') => {
    setActionLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE}/knowledge-review/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      });
      if (res.status === 404) {
        message.error('Record not found (may have already been reviewed)');
        await fetchPending();
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      message.success(action === 'approve' ? 'Record approved' : 'Record rejected');
      setRecords((prev) => prev.filter((r) => r.id !== id));
    } catch {
      message.error(`Failed to ${action} record`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <DatabaseOutlined />
            Pending Knowledge Review
          </Space>
        </Title>
        <Paragraph type="secondary">
          These fix records were flagged during submission because they had low similarity
          to existing knowledge entries (potential outliers or poisoning attempts).
          Review and approve or reject each one.
        </Paragraph>
      </div>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : records.length === 0 ? (
          <Empty
            description={
              <Text type="secondary">
                No pending records — the knowledge queue is clear.
              </Text>
            }
          />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={records}
            renderItem={(record) => (
              <List.Item
                key={record.id}
                actions={[
                  <Button
                    key="approve"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={actionLoading[record.id]}
                    onClick={() => handleAction(record.id, 'approve')}
                  >
                    Approve
                  </Button>,
                  <Button
                    key="reject"
                    danger
                    icon={<CloseOutlined />}
                    loading={actionLoading[record.id]}
                    onClick={() => handleAction(record.id, 'reject')}
                  >
                    Reject
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>
                        {record.vehicle_year} {record.vehicle_make} {record.vehicle_model}
                      </Text>
                      <Tag color="warning">{record.mileage_band}</Tag>
                      {record.dtc_codes && (
                        <Tag color="orange">{record.dtc_codes}</Tag>
                      )}
                      <Tag color="blue">{record.outcome}</Tag>
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Submitted: {new Date(record.created_at).toLocaleString()}
                      {record._score != null && (
                        <> &middot; Max similarity at write: {record._score.toFixed(3)}</>
                      )}
                    </Text>
                  }
                />
                <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Symptom">
                    {record.symptom_text}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cause">
                    {record.cause_text}
                  </Descriptions.Item>
                  <Descriptions.Item label="Correction">
                    {record.correction_text}
                  </Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default KnowledgeReviewPage;
